// Utils
import { asObject, toArray, prop, getMany } from '../../../common/utils/immutableHelper';
import { searchForIcon } from '../../components/elementIcon/elementIconService';
import { isBoard } from '../../../common/elements/utils/elementTypeUtils';
import makeStringIcon from '../../components/elementIcon/makeStringIconDef';
import { getFirstIconString } from '../../components/elementIcon/elementIconUtils';
import { updateElement } from '../actions/elementActions';
import { boardVisibleElementGraphSelector, elementGraphSelector } from '../selectors/elementGraphSelector';
import { getClosestAncestorBoardId, getElement } from '../../../common/elements/utils/elementTraversalUtils';
import { getChildrenViaGraph } from '../../../common/elements/utils/elementGraphUtils';
import { getElements } from '../selectors/elementsSelector';
import {
    getCanvasOriginCoordinates,
    getElementId,
    getXPosition,
    getYPosition,
} from '../../../common/elements/utils/elementPropertyUtils';
import { sendBoardIconSetEvent } from '../../components/elementIcon/elementIconAnalyticsUtils';
import { pickUnusedIcon } from './boardActionsUtil';
import { combineLists, combineUsedAndGeneratedColorCombos, getUsedColorCombos } from './colorComboPaletteUtils';
import { setCanvasInitialSize } from '../../canvas/store/canvasActions';
import { isColorCombo } from '../../../common/colors/colorComboUtils';

// Selectors
import { generateColorComboPalette, getCurrentBoardColorComboList } from './boardSelector';
import currentBoardSuggestedColorsSelector from '../../workspace/toolbar/selectionTools/colorTools/currentBoardSuggestedColorsSelector';

// Errors
import CanvasSizeError from '../../canvas/CanvasSizeError';
import { manuallyReportError } from '../../analytics/rollbarService';

// Constants
import * as TYPES from '../../../common/elements/elementConstants';
import { COLORS } from '../../../common/colors/colorConstants';
import { ICON_SEARCH_MODES } from '../../../common/icons/iconConstants';
import { ELEMENT_UPDATE_TYPE } from '../../../common/elements/elementConstants';
import { USED_COLOR_COMBOS_UPDATE } from './colorComboPaletteActionTypes';
import { MAXIMUM_CANVAS_ORIGIN, MINIMUM_CANVAS_ORIGIN } from '../../../common/elements/utils/elementPositionUtils';

export const DEFAULT_COLORS_ARRAY = Object.values(COLORS)
    .filter((color) => color.name !== COLORS.NONE.name)
    .map((color) => color.name);

const COLOR_INDEX_MAX = 100;
let colorIndex = Math.floor(Math.random() * COLOR_INDEX_MAX);

const getPeerBoards = (element, state) => {
    const elementId = getElementId(element);
    const elements = getElements(state);

    const parentBoardId = getClosestAncestorBoardId(elements, elementId);
    const elementGraph = boardVisibleElementGraphSelector(state);

    const peerBoards = getChildrenViaGraph({ elements, elementGraph, parentId: parentBoardId }).filter(isBoard);

    return peerBoards;
};

const getFallbackNumber = (peerBoards) => {
    if (!peerBoards) return 1;
    const boardCount = peerBoards.size;

    return boardCount.toString();
};

const makeFallbackBoardIcon = ({ title, peerBoards }) => {
    const iconString = getFirstIconString(title) || getFallbackNumber(peerBoards);
    const stringIcon = makeStringIcon(iconString);

    return stringIcon.toJS();
};

export const getColor = (title, state, colorList = []) => {
    const matchedColorWord = DEFAULT_COLORS_ARRAY.find((color) => (title || '').toUpperCase().indexOf(color) >= 0);
    if (matchedColorWord) return matchedColorWord;

    colorIndex = (1 + colorIndex) % COLOR_INDEX_MAX;

    const selectionList = combineLists(currentBoardSuggestedColorsSelector(state), colorList);
    return selectionList[colorIndex % selectionList.length];
};

export const getBoardDecoration = async ({ id, title, state }) => {
    const {
        data: { icons, isFallback },
    } = await searchForIcon(title, 4, ICON_SEARCH_MODES.BOARD_CREATE);

    const element = state.getIn(['elements', id]);
    const peerBoards = getPeerBoards(element, state);

    const unusedIcon = pickUnusedIcon(icons, peerBoards);

    const icon = unusedIcon || makeFallbackBoardIcon({ title, peerBoards });

    sendBoardIconSetEvent({
        boardId: id,
        icon,
        searchQuery: title,
        setMode: ICON_SEARCH_MODES.BOARD_CREATE,
        isFallback: isFallback || !unusedIcon,
    });

    return {
        icon,
    };
};

// get a new colorCombo list and add it to element state
export const regenerateColorCombo =
    ({ id }) =>
    async (dispatch, getState) => {
        const colorComboList = combineUsedAndGeneratedColorCombos(getState);
        dispatch(
            updateElement({
                id,
                changes: {
                    colorComboList,
                },
                transactionId: -1,
            }),
        );
    };

// update the used colorCombo list in app state
export const updateAppStateUsedColorCombos =
    ({ id }) =>
    async (dispatch, getState) => {
        dispatch({
            type: USED_COLOR_COMBOS_UPDATE,
            usedColorCombos: getUsedColorCombos(getState, id),
        });
    };

// update the used colorCombo list in element state
export const updateElementUsedColorCombos =
    ({ id }) =>
    async (dispatch, getState) => {
        const used = getUsedColorCombos(getState, id);
        dispatch(
            updateElement({
                id,
                changes: {
                    usedColorCombos: used,
                },
                transactionId: -1,
            }),
        );
        return used;
    };

export const initializeBoard =
    ({ id, title, currentColor, currentSecondaryColor, transactionId }) =>
    async (dispatch, getState) => {
        // Force a local update prior to the icon being set
        dispatch(
            updateElement({
                type: TYPES.ELEMENT_UPDATE,
                id,
                changes: {
                    title,
                },
                transactionId,
                sync: false,
            }),
        );

        dispatch({
            type: TYPES.ELEMENT_ICON_FIND,
            id,
            title,
        });

        const state = getState();
        let color = currentColor;
        let secondaryColor = currentSecondaryColor;
        // get color combo list from element state
        const currentBoardColorComboList = toArray(getCurrentBoardColorComboList(state));
        // if not in element state, generate a new color combo list
        const currentBoardList = currentBoardColorComboList ?? generateColorComboPalette(state);

        // only check for primary color here because its important to keep secondary null if there is no secondary passed in
        // this allows for black/white based on the primary color
        if (!color) {
            const colorItem = getColor(title, state, currentBoardList);
            const isCombo = isColorCombo(colorItem);
            color = isCombo ? prop('primary', colorItem) : colorItem;
            secondaryColor = isCombo ? prop('secondary', colorItem) : null;
        }

        const decoration = await getBoardDecoration({
            id,
            title,
            state: getState(),
        });

        dispatch({ type: TYPES.ELEMENT_ICON_FIND_SUCCESS, id });

        dispatch(
            updateElement({
                type: TYPES.ELEMENT_UPDATE,
                id,
                changes: {
                    ...decoration,
                    title,
                    color,
                    secondaryColor,
                    autoColorIndex: colorIndex,
                },
                transactionId,
            }),
        );

        // update current board color combo palette
        if (!currentBoardColorComboList) {
            const elements = getElements(state);
            const parentBoardId = getClosestAncestorBoardId(elements, id);
            dispatch(
                updateElement({
                    id: parentBoardId,
                    changes: {
                        colorComboList: currentBoardList,
                    },
                    transactionId,
                }),
            );
        }
    };

/**
 * Updates the canvas origin for a given board ID.
 */
export const updateBoardCanvasOrigin =
    ({ id, canvasOrigin, ...rest }) =>
    (dispatch, getState) => {
        const state = getState();
        const elements = getElements(state);

        const element = getElement(elements, id);
        const initialCanvasOrigin = asObject(getCanvasOriginCoordinates(element));

        if (canvasOrigin.x < MINIMUM_CANVAS_ORIGIN || canvasOrigin.y < MINIMUM_CANVAS_ORIGIN) {
            // Error debugging - Determine if any of the elements are already beyond the canvas origin.
            //  If so, the board is in an errored state and can't be recovered from without further
            //  intervention.  Somehow elements have ended up beyond the canvas origin.
            const elementGraph = elementGraphSelector(state);
            const boardChildIds = elementGraph[id] || [];
            const boardChildren = getMany(boardChildIds, elements);

            const inaccessibleElements = new Set();

            boardChildren.forEach((child) => {
                const childId = getElementId(child);
                const xPosition = getXPosition(child);
                const yPosition = getYPosition(child);

                const elementIsNotAccessible =
                    (xPosition < initialCanvasOrigin.x && xPosition < MINIMUM_CANVAS_ORIGIN) ||
                    (yPosition < initialCanvasOrigin.y && yPosition < MINIMUM_CANVAS_ORIGIN);

                if (elementIsNotAccessible) {
                    inaccessibleElements.add(childId);
                }
            });

            if (inaccessibleElements.size !== 0) {
                manuallyReportError({
                    errorMessage: 'Board is in a broken state - elements are beyond the canvas origin',
                    custom: {
                        currentBoardId: id,
                        elementIds: [...inaccessibleElements].join(','),
                    },
                    getState,
                });
            }

            throw new CanvasSizeError(`Cannot extend the canvas origin beyond -500,-500`, {
                friendlyMessage: "The canvas can't grow this far",
                tip: 'Try moving the content towards the bottom right',
                details: {
                    canvasOrigin,
                },
            });
        }

        if (canvasOrigin.x > MAXIMUM_CANVAS_ORIGIN || canvasOrigin.y > MAXIMUM_CANVAS_ORIGIN) {
            throw new CanvasSizeError(`Cannot extend the canvas origin beyond 0,0`, {
                friendlyMessage: 'An error has occurred with the canvas size',
                details: {
                    canvasOrigin,
                },
            });
        }

        dispatch(setCanvasInitialSize({ boardId: id, canvasOrigin: initialCanvasOrigin }));

        dispatch(
            updateElement({
                id,
                updateType: ELEMENT_UPDATE_TYPE.CANVAS_ORIGIN,
                changes: {
                    canvasOrigin,
                },
                ...rest,
            }),
        );
    };
